/* eslint-disable @typescript-eslint/no-explicit-any */
import {
	BillingType,
	ClaimServiceProvidedFor,
	ClaimStatus,
	EnvKeys,
	UserRole,
	UsesPerYearDefinition,
	FILE_SIZE_LIMITS
} from "./consts";
import {
	Claim as BffClaim,
	ClaimsService as BffClaimService,
	ClaimsStatus as BffClaimsStatus,
	Billing,
	BillingProviderType,
	MemberType,
	ClaimsDocument,
	DocumentCategory,
	ClaimPerson,
	Checklist
} from "./api/protos/messages/claims.message_pb";
import {
	Claim,
	ClaimDocument,
	FileIdentifier as FileIdentifierType,
	ClaimServiceBilling,
	JwtToken,
	LegalBenefit,
	DisplayCardStatementProps,
	ClaimServiceBillingType,
	ErrorResponse,
	ChecklistItem,
	ClaimService,
	User,
	CustomColumnFilterOption
} from "./types";
import { DocumentCategory as DocumentCategoryLocal } from "./consts";
import { merge } from "lodash";
import {
	LegalBenefit as BffLegalBenefit,
	BillingTypeEnum,
	UsesPerYearDefinition as BffUsesPerYearDefinition
} from "./api/protos/messages/benefits.message_pb";
import { Decimal } from "./api/protos/google/type/decimal_pb";
import { Date as CoreDate } from "./api/protos/messages/core.message_pb";
import { downloadDocStream } from "./api/files";

/**
 * Helper function to convert a unix timestamp to the local datetime
 * @param timestamp Unix timestamp
 * @returns Date object in local time
 */
export const convertUnixTimestampToLocalDate = (timestamp: number): Date => {
	const utcDate: Date = new Date(timestamp * 1000);

	return utcDate;
};

/**
 * Helper function to convert a unix timestamp to the UTC date with time stripped out
 * @param timestamp Unix timestamp
 * @returns Date object in UTC time
 */
export const convertUnixTimestampToUTCDate = (timestamp: number): Date => {
	const utcDate: Date = new Date(timestamp * 1000);

	return new Date(utcDate.getUTCFullYear(), utcDate.getUTCMonth(), utcDate.getUTCDate());
};

/**
 * Helper function to convert a unix timestamp to the UTC date with time stripped out
 * Also blatantly copy pastes a misleading tooltip for different implementation
 * @param timestamp Unix timestamp
 * @returns Date object in UTC time
 */
export const convertDateToUTCDate = (date: Date): Date => {
	return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};

export const convertProtoDateToDateTime = (date: CoreDate | undefined): Date => {
	if (!date) return new Date();

	const newDate: Date = new Date(date.getYear(), date.getMonth() - 1, date.getDay());

	return newDate;
};

export const convertDateToProtoDateTime = (date: Date): CoreDate => {
	const newDate: CoreDate = new CoreDate();
	newDate.setDay(date.getDate());
	newDate.setMonth(date.getMonth() + 1);
	newDate.setYear(date.getFullYear());

	return newDate;
};

/**
 * Helper function to return the current Date in UTC
 * @returns Date object in UTC Timezone
 */
export const utcNow = (): Date => new Date(`${new Date().toString()} UTC`);

export const getClaimStatusFromBffStatus = (bffClaimsStatus: BffClaimsStatus): ClaimStatus => {
	switch (bffClaimsStatus) {
		case BffClaimsStatus.DRAFT:
			return ClaimStatus.Draft;
		case BffClaimsStatus.SUBMITTED:
			return ClaimStatus.Submitted;
		case BffClaimsStatus.IN_REVIEW:
			return ClaimStatus.InReview;
		case BffClaimsStatus.APPROVED:
			return ClaimStatus.Approved;
		case BffClaimsStatus.DENIED:
			return ClaimStatus.Denied;
		case BffClaimsStatus.ARCHIVED:
			return ClaimStatus.Archived;
		case BffClaimsStatus.CANCELLED:
			return ClaimStatus.Cancelled;
		case BffClaimsStatus.CLOSED_NOT_PAID:
			return ClaimStatus.ClosedNotPaid;
		case BffClaimsStatus.CLOSED_NOT_PAID_GOODWILL:
			return ClaimStatus.ClosedNotPaidGoodwill;
		case BffClaimsStatus.CLOSED_PAID:
			return ClaimStatus.ClosedPaid;
		case BffClaimsStatus.QUEUED_FOR_PAYMENT:
			return ClaimStatus.QueuedForPayment;
		case BffClaimsStatus.PENDING_PROVIDER:
			return ClaimStatus.PendingProvider;
		case BffClaimsStatus.PENDING_VERIFICATION:
			return ClaimStatus.PendingVerification;
		case BffClaimsStatus.PENDING_INTERNAL_REVIEW:
			return ClaimStatus.PendingInternalReview;
		case BffClaimsStatus.DUPLICATE:
			return ClaimStatus.Duplicate;
		default:
			console.error(`Attempting to fetch Claim Status for unknown value => ${bffClaimsStatus}`);
			return ClaimStatus.Draft;
	}
};

export const getBffStatusFromClaimStatus = (claimsStatus: ClaimStatus): BffClaimsStatus => {
	switch (claimsStatus) {
		case ClaimStatus.Draft:
			return BffClaimsStatus.DRAFT;
		case ClaimStatus.Submitted:
			return BffClaimsStatus.SUBMITTED;
		case ClaimStatus.InReview:
			return BffClaimsStatus.IN_REVIEW;
		case ClaimStatus.Approved:
			return BffClaimsStatus.APPROVED;
		case ClaimStatus.Denied:
			return BffClaimsStatus.DENIED;
		case ClaimStatus.Archived:
			return BffClaimsStatus.ARCHIVED;
		case ClaimStatus.Cancelled:
			return BffClaimsStatus.CANCELLED;
		case ClaimStatus.ClosedNotPaid:
			return BffClaimsStatus.CLOSED_NOT_PAID;
		case ClaimStatus.ClosedNotPaidGoodwill:
			return BffClaimsStatus.CLOSED_NOT_PAID_GOODWILL;
		case ClaimStatus.ClosedPaid:
			return BffClaimsStatus.CLOSED_PAID;
		case ClaimStatus.QueuedForPayment:
			return BffClaimsStatus.QUEUED_FOR_PAYMENT;
		case ClaimStatus.PendingProvider:
			return BffClaimsStatus.PENDING_PROVIDER;
		case ClaimStatus.PendingVerification:
			return BffClaimsStatus.PENDING_VERIFICATION;
		case ClaimStatus.PendingInternalReview:
			return BffClaimsStatus.PENDING_INTERNAL_REVIEW;
		case ClaimStatus.Duplicate:
			return BffClaimsStatus.DUPLICATE;
		default:
			console.error(`Attempting to fetch BFF Claim Status for unknown value => ${claimsStatus}`);
			return BffClaimsStatus.DRAFT;
	}
};

export const getChecklistFromBffChecklist = (checklistItems: Checklist[]): ChecklistItem[] => {
	return checklistItems.map((item) => ({
		id: item.getId(),
		type: item.getChecklistType(),
		claimId: item.getClaimId(),
		label: item.getLabel(),
		value: item.getValue()
	}));
};

/**
 * Helper function to init a new, empty Claims object with default values
 * @returns Claims object
 */
export const initEmptyClaim = (): Claim => {
	const newClaim: Claim = {
		dateOfInitialConsultation: new Date(),
		finalDateOfService: new Date(),
		assignedAnalyst: undefined,
		claimServices: [],
		documents: [],
		billingAdditionalFees: new Decimal().setValue("0.00").getValue(),
		billingtotal: new Decimal().setValue("0.00").getValue(),
		otherNonCoveredFees: new Decimal().setValue("0.00").getValue(),
		otherNonCoveredHours: new Decimal().setValue("0.00").getValue(),
		checklistItems: [],
		checklistComplete: false,
		status: ClaimStatus.Draft
	};

	return newClaim;
};

const getServiceProvidedFor = (memberType: MemberType): ClaimServiceProvidedFor => {
	switch (memberType) {
		case MemberType.PRIMARY:
			return ClaimServiceProvidedFor.PrimaryMember;
		case MemberType.SPOUSE:
			return ClaimServiceProvidedFor.Spouse;
		case MemberType.DOMESTIC_PARTNER:
			return ClaimServiceProvidedFor.DomesticPartner;
		case MemberType.DEPENDANT_CHILD:
			return ClaimServiceProvidedFor.DependentChild;
		case MemberType.ELDER_PARENT:
			return ClaimServiceProvidedFor.ElderParent;
		case MemberType.OTHER:
			return ClaimServiceProvidedFor.Other;
	}
};

export const convertBffClaimToLocalClaim = (bffClaim: BffClaim): Claim => {
	let bffServices: Array<BffClaimService> = bffClaim.getServicesList();
	let claimServices: Array<ClaimService> = bffServices.map((bffSvc: BffClaimService) => {
		let newSvc: ClaimService = {
			claimId: bffSvc.getClaimId(),
			legalBenefitId: bffSvc.getLegalBenefitId(),
			serviceProvidedFor: getServiceProvidedFor(bffSvc.getServiceProvidedFor()),
			recipientAge: bffSvc.hasRecipientAge() ? bffSvc.getRecipientAge() : undefined,
			serviceId: bffSvc.getId(),
			finalDisposition: bffSvc.hasFinalDisposition() ? bffSvc.getFinalDisposition() : undefined,
			consultationCompleted: bffSvc.hasConsultationCompleted() ? bffSvc.getConsultationCompleted() : undefined,
			officeWorkCompleted: bffSvc.hasOfficeWorkCompleted() ? bffSvc.getOfficeWorkCompleted() : undefined,
			negotiationCorrespondenceCompleted: bffSvc.hasNegotiationCorrespondenceCompleted()
				? bffSvc.getNegotiationCorrespondenceCompleted()
				: undefined,
			lawsuitCaseFiledRespondedCompleted: bffSvc.hasLawsuitCaseFiledRespondedCompleted()
				? bffSvc.getLawsuitCaseFiledRespondedCompleted()
				: undefined,
			courtAppearanceCompleted: bffSvc.hasCourtAppearanceCompleted() ? bffSvc.getCourtAppearanceCompleted() : undefined,
			caseFullyCompleted: bffSvc.hasCaseFullyCompleted() ? bffSvc.getCaseFullyCompleted() : undefined,
			billing: bffSvc
				.getBillingList()
				.map((b: Billing) => {
					let newBilling: ClaimServiceBilling | undefined = undefined;
					const ids = {
						claimId: bffSvc.getClaimId(),
						serviceId: bffSvc.getId(),
						id: b.getId()
					};

					switch (b.getBillingCase()) {
						case Billing.BillingCase.FLAT: {
							const hours = "N/A";
							const totalAmount = b.getFlat()?.getTotalAmount()?.getValue() ?? "0.00";
							newBilling = {
								...ids,
								type: ClaimServiceBillingType.Flat,
								discountedRate: b.getFlat()?.getDiscountedRate()?.getValue() ?? "0.00",
								standardRate: b.getFlat()?.getStandardRate()?.getValue() ?? "0.00",
								hours,
								totalAmount
							};
							break;
						}
						case Billing.BillingCase.FLAT_HIGH: {
							const hours = "N/A";
							const totalAmount = b.getFlatHigh()?.getTotalAmount()?.getValue() ?? "0.00";
							newBilling = {
								...ids,
								type: ClaimServiceBillingType.FlatHigh,
								discountedRate: b.getFlatHigh()?.getDiscountedRate()?.getValue() ?? "0.00",
								standardRate: b.getFlatHigh()?.getStandardRate()?.getValue() ?? "0.00",
								hours,
								totalAmount
							};
							break;
						}
						case Billing.BillingCase.HOURLY: {
							const hourly = b.getHourly();
							switch (hourly?.getProvidertype()) {
								case BillingProviderType.ATTORNEY: {
									newBilling = {
										...ids,
										type: ClaimServiceBillingType.HourlyAttorney,
										standardRate: hourly.getStandardRate()?.getValue() ?? "",
										discountedRate: hourly.getDiscountedRate()?.getValue() ?? "",
										hours: hourly.getHours()?.getValue()
											? parseFloat(hourly.getHours()!.getValue()).toFixed(2)
											: undefined,
										totalAmount: hourly.getTotalAmount()?.getValue() ?? ""
									};

									break;
								}
								case BillingProviderType.PARALEGAL: {
									newBilling = {
										...ids,
										type: ClaimServiceBillingType.HourlyParalegal,
										standardRate: hourly.getStandardRate()?.getValue() ?? "",
										discountedRate: hourly.getDiscountedRate()?.getValue() ?? "",
										hours: hourly.getHours()?.getValue()
											? parseFloat(hourly.getHours()!.getValue()).toFixed(2)
											: undefined,
										totalAmount: hourly.getTotalAmount()?.getValue() ?? ""
									};
									break;
								}
							}
							break;
						}
					}
					return newBilling;
				})
				.filter((item) => item != null) as ClaimServiceBilling[]
		};

		return newSvc;
	});

	let claimsDocuments: Array<ClaimDocument> = bffClaim.getDocumentsList().map((doc: ClaimsDocument) => {
		const document = doc.getFile();
		let category = documentCategoryTypeToLocal(doc.getCategory());
		const fileSize = doc.getFileSize() ?? 0;

		let file: FileIdentifierType = {
			fileId: document?.getFileId() ?? "",
			fileName: document?.getFileName() ?? "",
			fileSize: fileSize
		};

		// Make sure isDeleted and isGenerated are explicitly included in the returned object
		return {
			file,
			category,
			isDeleted: doc.getIsDeleted(),
			fileSize: fileSize,
			isGenerated: doc.getIsGenerated()
		} as ClaimDocument;
	});

	let convertedClaim: Partial<Claim> = {
		claimId: bffClaim.getId(),
		caseId: bffClaim.getCaseId(),
		lampCaseId: bffClaim.getLampCaseId(),
		atty: bffClaim.getAtty(),
		attyActive: bffClaim.getAttyActive(),
		member: bffClaim.getMember(),
		finalDateOfService: convertProtoDateToDateTime(bffClaim.getFinalDateOfService()),
		assignedAnalyst: bffClaim.hasAssignedAnalyst() ? bffClaim.getAssignedAnalyst() : undefined,
		dateOfInitialConsultation: convertProtoDateToDateTime(bffClaim.getDateOfInitialConsultation()),
		status: getClaimStatusFromBffStatus(bffClaim.getStatus()),
		checklistItems: getChecklistFromBffChecklist(bffClaim.getChecklistItemsList()),
		checklistComplete: bffClaim.getChecklistComplete(),
		cancelReason: bffClaim.getCancelReason(),
		denyReason: bffClaim.getDenyReason(),
		claimServices: claimServices,
		documents: claimsDocuments,
		otherNonCoveredFees: bffClaim.getBillingNonCoveredFees()?.getValue(),
		otherNonCoveredHours: bffClaim.getBillingNonCoveredServicesHours()?.getValue(),
		billingtotal: bffClaim.getMarketValueTotal()?.getValue(),
		dueDate: bffClaim.getDueDate() ? convertProtoDateToDateTime(bffClaim.getDueDate()) : undefined,
		cleanDate: bffClaim.getCleanDate() ? convertProtoDateToDateTime(bffClaim.getCleanDate()) : undefined,
		submittedAt:
			bffClaim.getSubmittedAt() === 0 ? undefined : convertUnixTimestampToLocalDate(bffClaim.getSubmittedAt()),
		approvedAt: bffClaim.getApprovedAt() === 0 ? undefined : convertUnixTimestampToLocalDate(bffClaim.getApprovedAt()),
		receivedOn: bffClaim.hasReceivedOn() ? convertDateToUTCDate(new Date(bffClaim.getReceivedOn())) : undefined,
		legalMatterState: bffClaim.getLegalMatterState(),
		isHeldForAudit: bffClaim.hasIsHeldForAudit() ? bffClaim.getIsHeldForAudit() : undefined
	};

	// For development purposes, please fill this out later
	const retVal = merge(initEmptyClaim(), convertedClaim);
	return retVal;
};

export const documentCategoryTypeToLocal = (category: DocumentCategory): DocumentCategoryLocal => {
	switch (category) {
		case DocumentCategory.INVOICES:
		case 0: // Handle numeric value directly
			return DocumentCategoryLocal.Invoices;
		case DocumentCategory.SUPPORTING_DOCUMENTS:
		case 1: // Handle numeric value directly
			return DocumentCategoryLocal.SupportingDocuments;
		case DocumentCategory.PROOF_OF_FULL_TIME_SCHOOL_ENROLLMENT:
		case 2: // Handle numeric value directly
			return DocumentCategoryLocal.ProofOfFullTimeSchoolEnrollment;
		case DocumentCategory.CLAIM_FORMS:
		case 3: // Handle numeric value directly since DocumentCategory.CLAIM_FORMS is undefined due to syntax error
			return DocumentCategoryLocal.ClaimForms;
		case DocumentCategory.CCS:
		case 4: // Handle numeric value directly
			return DocumentCategoryLocal.Ccs;
		case DocumentCategory.DENIED_DOCUMENTS:
		case 5:
			return DocumentCategoryLocal.Denied;
		case DocumentCategory.AUDIT_DOCUMENTS:
		case 6:
			return DocumentCategoryLocal.AuditDocuments;
		default:
			console.warn(`Unknown document category: ${category}`);
			return DocumentCategoryLocal.Invoices; // Default fallback
	}
};

export const convertBffLegalBenefitToLocalLegalBenefit = (bffBenefit: BffLegalBenefit): LegalBenefit => {
	let usesPerYearDef = undefined;
	switch (bffBenefit.getUsesPerYearDefinition()) {
		case BffUsesPerYearDefinition.TOTAL: {
			usesPerYearDef = UsesPerYearDefinition.Total;
			break;
		}
		case BffUsesPerYearDefinition.PER_COVERED_PERSON: {
			usesPerYearDef = UsesPerYearDefinition.PerCoveredPerson;
			break;
		}
	}

	let billingType = undefined;
	switch (bffBenefit.getBillingtype()) {
		case BillingTypeEnum.ESTATE_PLANNING: {
			billingType = BillingType.EstatePlanning;
			break;
		}
		case BillingTypeEnum.FIXED_HIGH_VALUE: {
			billingType = BillingType.FixedHighValue;
			break;
		}
		case BillingTypeEnum.FIXED_LOW_VALUE: {
			billingType = BillingType.FixedLowValue;
			break;
		}
		case BillingTypeEnum.HOURLY: {
			billingType = BillingType.Hourly;
			break;
		}
		case BillingTypeEnum.HOURLY_OR_FIXED: {
			billingType = BillingType.HourlyOrFixed;
			break;
		}
	}

	return {
		benefitTitle: bffBenefit.getMatterTitle(),
		id: bffBenefit.getId(),
		matterBillingCode: bffBenefit.getMatterBillingCode(),
		matterBenefitDescription: bffBenefit.getMatterBenefitDescription(),
		matterPaymentParameters: bffBenefit.getMatterPaymentParameters(),
		hourlyRateCap: bffBenefit.getHourlyRate()?.getValue(),
		totalHoursCap: bffBenefit.getTotalHours()?.getValue(),
		totalAmountCap: bffBenefit.getTotalAmount()?.getValue(),
		raterMaxRateCap: bffBenefit.getRaterMaxRate()?.getValue(),
		matterLimitations: bffBenefit.getMatterLimitations(),
		paidInFull: bffBenefit.getPaidInFull(),
		usesPerYear: bffBenefit.getUsesPerYear(),
		usesPerYearDefinition: usesPerYearDef,
		matterSpecialInstructions: bffBenefit.getMatterSpecialInstructions(),
		providerInstructions: bffBenefit.getProviderInstructions(),
		coversPrimaryMember: bffBenefit.getPrimarymember(),
		requireSupportingDocument: bffBenefit.getRequireSupportingDocuments(),
		coversSpouse: bffBenefit.getSpouse(),
		coversDomesticPartner: bffBenefit.getDomesticpartner(),
		coversElderParent: bffBenefit.getElderparent(),
		billingType,
		lgPolicyId: bffBenefit.getLgpolicyid(),
		hasOverride: bffBenefit.getHasOverride(),
		providerStandardHourlyRate: bffBenefit.getProviderStandardHourlyRate()?.getValue(),
		providerStandardFlatRate: bffBenefit.getProviderStandardFlatRate()?.getValue()
	};
};

export const toLocalISODateString = (date: Date) => {
	const year = date.getFullYear();
	const month = (date.getMonth() + 1).toString().padStart(2, "0");
	const day = date.getDate().toString().padStart(2, "0");

	return `${year}-${month}-${day}`;
};

/**
 * This is a helper function used to adjust DatePicker values in the following scenarios:
 * * Chakra-UI DatePicker is a wrapper around the native `<input type="date" />` element, this element
 * 		selects the chosen date in UTC. However, when creating a JS Date object, it converts from UTC to local time.
 * 		This creates the scenario in which the user selects 4/24 in UTC which can resolve to 4/23 because of UTC offset
 * @param date string date or Date object
 * @returns Date object with UTC offset added back to the Date
 */
export const adjustDatePickerOffset = (date: string | Date): Date => {
	let rawDate: Date;
	// If the input is of type string, create new Date
	if (typeof date === "string") {
		rawDate = new Date(date);
	} else rawDate = date;

	// Convert the Date to UTC
	// Do a .toISOString and parse out the year, month, and day
	// Set the time portion to 0's
	// YYYY-MM-dd
	const utcDateString: string = rawDate.toISOString().split("T")[0];
	const utcDateStringParts: Array<string> = utcDateString.split("-");
	let newDate: Date = new Date();
	newDate.setFullYear(
		parseInt(utcDateStringParts[0]),
		parseInt(utcDateStringParts[1]) - 1,
		parseInt(utcDateStringParts[2])
	);

	// Simply reset the time portion to 00:00:00
	newDate.setHours(0);
	newDate.setMinutes(0);
	newDate.setSeconds(0);

	return newDate;
};

/**
 * Given a Date in local Timezone, converts it to UTC and then returns an Int64
 * representation of the Date. Mostly used for BFF Operations
 * @param date Date in local Timezone
 * @returns Int64 representation of UTC Date
 */
export const dateToInt64UTC = (date: Date): number => {
	// Retrieve the UTC Date
	let newDate: Date = adjustDatePickerOffset(date);
	let utcResult = parseInt((newDate.getTime() / 1000).toString());

	return utcResult;
};

/**
 * Helper function to pull values from ENV
 * @param key The ENV Key to pull
 * @returns Corresponding value or empty string
 */
export const getEnvValue = (key: EnvKeys): string => {
	switch (key) {
		case EnvKeys.JwtCookieKey:
			return process.env.REACT_APP_JWT_COOKIE_KEY ?? "";
		case EnvKeys.RefreshCookieKey:
			return process.env.REACT_APP_REFRESH_COOKIE_KEY ?? "";
		case EnvKeys.UserCookieKey:
			return process.env.REACT_APP_USER_COOKIE_KEY ?? "";
		case EnvKeys.LampUrl:
			return process.env.REACT_APP_LAMP_URL ?? "";
		case EnvKeys.LampAttorneyUrl:
			return process.env.REACT_APP_LAMP_ATTORNEY_URL ?? "";
		case EnvKeys.PassUrl:
			return process.env.REACT_APP_PASS_URL ?? "";
		case EnvKeys.LampCaseUrl:
			return process.env.REACT_APP_LAMP_CASE_URL ?? "";
		case EnvKeys.MemberPassUrl:
			return process.env.REACT_APP_MEMBER_PASS_URL ?? "";
		case EnvKeys.NetworkRate:
			return process.env.REACT_APP_NETWORK_RATE ?? "";
		case EnvKeys.NetworkRateParalegal:
			return process.env.REACT_APP_NETWORK_RATE_PARALEGAL ?? "";
		case EnvKeys.AzureClientId:
			return process.env.REACT_APP_AZURE_AD_CLIENT_ID ?? "";
		case EnvKeys.AzureTenantId:
			return process.env.REACT_APP_AZURE_AD_TENANT_ID ?? "";
		case EnvKeys.SSODomains:
			return process.env.REACT_APP_SSO_DOMAINS ?? "";
		default:
			return "";
	}
};

export const createExternalLinkFromEnv = (key: EnvKeys, id: number | string): string => {
	const base_url: string = getEnvValue(key);
	return `${base_url}${id.toString()}`;
};

export const createAttorneyUpdateLinkFromEnv = (attyId: number | string, token: string): string => {
	const base_url: string = getEnvValue(EnvKeys.LampUrl);
	return `${base_url}self-service/${attyId.toString()}?token=${token}`;
};

/**
 * Helper function to pull the Token from the JwtToken Cookie
 * Mainly used for API calls
 * @returns Found token or null if not found
 */
export const getJwtCookie = (): string | null => {
	const name: string = getEnvValue(EnvKeys.JwtCookieKey);
	const nameLenPlus = name.length + 1;
	const foundCookie: string | null =
		document.cookie
			.split(";")
			.map((c) => c.trim())
			.filter((cookie) => {
				return cookie.substring(0, nameLenPlus) === `${name}=`;
			})
			.map((cookie) => {
				return decodeURIComponent(cookie.substring(nameLenPlus));
			})[0] || null;

	if (foundCookie) {
		const jwtCookie: JwtToken = JSON.parse(foundCookie);
		return jwtCookie.token;
	}

	return null;
};

/**
 * Helper function to return the JWT Token for various API calls
 * @returns JWT Token String
 * @throws "unauthenticated" error if no token is present
 */
export const getApiToken = (): string => {
	const token = getJwtCookie();
	if (!token) {
		throw new Error("unauthenticated");
	}

	return token;
};

/**
 * Helper function to convert a Claim object to a DisplayCardStatementProps object
 * @param claim Claim object
 * @returns DisplayCardStatementProps
 */
export function transformClaimToDisplayStatementData(
	claim: Claim,
	getServiceLabel: (claimService: any) => string
): DisplayCardStatementProps {
	let totalHours = 0;
	let totalNetworkCost = 0;
	let totalStandardCost = 0;

	// Helper function to calculate High Fixed Value percentage
	const calculateHighFixedValuePercentage = (service: any): number => {
		if (service.finalDisposition) {
			return 100;
		}

		let highestPercentage = 0;

		if (service.consultationCompleted) highestPercentage = Math.max(highestPercentage, 5);
		if (service.officeWorkCompleted) highestPercentage = Math.max(highestPercentage, 15);
		if (service.negotiationCorrespondenceCompleted) highestPercentage = Math.max(highestPercentage, 25);
		if (service.lawsuitCaseFiledRespondedCompleted) highestPercentage = Math.max(highestPercentage, 50);
		if (service.courtAppearanceCompleted) highestPercentage = Math.max(highestPercentage, 80);
		if (service.caseFullyCompleted) highestPercentage = Math.max(highestPercentage, 100);

		return highestPercentage;
	};

	// Helper function to get the highest work level description for tooltip
	const getHighestWorkLevel = (service: any): string => {
		if (service.caseFullyCompleted) return "Case Completed";
		if (service.courtAppearanceCompleted) return "Court Appearance(s)";
		if (service.lawsuitCaseFiledRespondedCompleted) return "Lawsuit/Case Filed or Responded To";
		if (service.negotiationCorrespondenceCompleted) return "Negotiation/Correspondence";
		if (service.officeWorkCompleted) return "Office Work";
		if (service.consultationCompleted) return "Consultation";
		return "None";
	};

	const getSelectedCaseWorkLevels = (service: any): string[] => {
		const selectedLevels: string[] = [];
		if (service.finalDisposition) selectedLevels.push("Final Disposition");
		if (service.consultationCompleted) selectedLevels.push("Consultation - 5%");
		if (service.officeWorkCompleted) {
			selectedLevels.push("Office Work (Document Review, Document Preparation, Research) - 15%");
		}
		if (service.negotiationCorrespondenceCompleted) selectedLevels.push("Negotiation/Correspondence - 25%");
		if (service.lawsuitCaseFiledRespondedCompleted) selectedLevels.push("Lawsuit/Case Filed or Responded To - 50%");
		if (service.courtAppearanceCompleted) selectedLevels.push("Court Appearance(s) - 80%");
		if (service.caseFullyCompleted) {
			selectedLevels.push(
				"Case Completed, Resulting In Documented Final Disposition (Decree, Settlement Agreement, Judgment) - 100%"
			);
		}
		return selectedLevels;
	};

	const billingItems = claim.claimServices
		.filter((service) => !!service.billing)
		.flatMap((service) =>
			service.billing!.map((billingItem) => {
				const isFlatRate =
					billingItem.type === ClaimServiceBillingType.Flat ||
					(billingItem.type === ClaimServiceBillingType.FlatHigh && !billingItem.hours);
				const providerType = isFlatRate
					? "N/A"
					: billingItem.type === ClaimServiceBillingType.HourlyAttorney
						? "Attorney"
						: billingItem.type === ClaimServiceBillingType.HourlyParalegal
							? "Paralegal"
							: "N/A";

				const hours: string | number = isFlatRate ? "N/A" : parseFloat(billingItem.hours ?? "0.00");
				let networkRate = parseFloat(billingItem.discountedRate ?? "0.00");
				const standardRate = parseFloat(billingItem.standardRate ?? "0.00");

				// Check if this is a High Fixed Value benefit and get the percentage
				let highFixedValueInfo = "";
				let totalAmount = parseFloat(billingItem.totalAmount ?? "0.00");
				let tooltipData = undefined;
				let isHighFixedValue = billingItem.type === ClaimServiceBillingType.FlatHigh;

				// Get the benefit to check its type
				const benefit = claim.claimServices.find((s) => s.serviceId === service.serviceId);
				if (benefit) {
					// If it's a High Fixed Value benefit with any completion status fields set
					const hasHighFixedValueFields =
						benefit.finalDisposition !== undefined ||
						benefit.consultationCompleted !== undefined ||
						benefit.officeWorkCompleted !== undefined ||
						benefit.negotiationCorrespondenceCompleted !== undefined ||
						benefit.lawsuitCaseFiledRespondedCompleted !== undefined ||
						benefit.courtAppearanceCompleted !== undefined ||
						benefit.caseFullyCompleted !== undefined;

					if (hasHighFixedValueFields) {
						isHighFixedValue = true;
						if (billingItem.type === ClaimServiceBillingType.FlatHigh) {
							const percentage = calculateHighFixedValuePercentage(benefit);
							highFixedValueInfo = ` (${percentage}% complete)`;

							// For High Fixed Value, recalculate the network maximum as percentage * network rate
							const maxTotal = (networkRate * percentage) / 100;
							const networkMaximum = networkRate;
							networkRate = maxTotal;

							// Create tooltip data
							tooltipData = {
								highestWorkLevel: getHighestWorkLevel(benefit),
								networkMaximum: networkMaximum.toFixed(2),
								percentage: percentage,
								calculatedTotal: maxTotal.toFixed(2)
							};
						}
					}
				}

				// If flat rate, display total as the fixed network rate
				const totalAmountString = totalAmount.toFixed(2);

				// Accumulate totals
				if (typeof hours == "number" && !isNaN(hours)) {
					totalHours += hours;
					totalNetworkCost += totalAmount;
					totalStandardCost += hours * standardRate;
				}
				if (typeof hours == "string" || (typeof hours == "number" && isNaN(hours))) {
					totalNetworkCost += totalAmount;
					totalStandardCost += standardRate;
				}

				return {
					serviceId: service.serviceId, // Keep the service ID
					service: getServiceLabel(service) + highFixedValueInfo, // Append High Fixed Value info
					recipient: service.serviceProvidedFor
						? service.serviceProvidedFor.toString() + (service.recipientAge ? ` (Age ${service.recipientAge})` : "")
						: "",
					providerType: providerType,
					hours: typeof hours == "string" ? "N/A" : hours.toFixed(2),
					networkRate: networkRate.toFixed(2),
					standardRate: standardRate.toFixed(2),
					totalAmount: totalAmountString,
					isHighFixedValue: isHighFixedValue,
					selectedCaseWorkLevels: isHighFixedValue ? getSelectedCaseWorkLevels(benefit) : [],
					tooltipData: tooltipData
				};
			})
		);

	return {
		data: {
			dates: {
				dateOfInitialConsultation: claim.dateOfInitialConsultation,
				finalDateOfService: claim.finalDateOfService
			},
			billingItems: billingItems ?? [],
			summary: {
				otherNonCoveredFees: claim.otherNonCoveredFees || "0.00",
				otherNonCoveredHours: claim.otherNonCoveredHours || "0.00",
				billingAdditionalFees: claim.billingAdditionalFees || "0.00",
				billingTotal: claim.billingtotal || "0.00",
				totalHours: totalHours > 0 ? totalHours.toFixed(2) : "",
				totalNetworkCost: totalNetworkCost.toFixed(2),
				totalStandardCost: totalStandardCost.toFixed(2),
				claimAndNonCoveredItems: claim.billingAdditionalFees ?? "0.00",
				marketValue: claim.billingtotal ?? "0.00"
			}
		}
	};
}

/**
 * Handle clicking a document link with enhanced error handling and logging
 * @param file_id The file id
 * @param toast Toast reference
 * @param openNewTab Whether or not to open a new tab. Defaults to `true`
 * @param downloadFileName Attempt to download the file with this name
 * @param onProgress Optional progress callback for download tracking
 */
export const handleDocClick = async (
	file_id: string,
	toast: any,
	openNewTab = true,
	downloadFileName?: string,
	onProgress?: (progress: number) => void
) => {
	console.log(`[handleDocClick] Starting file open process for file ID: ${file_id}`);

	try {
		console.log(`[handleDocClick] Calling downloadDocStream for file ID: ${file_id}`);
		const doc = await downloadDocStream(file_id, onProgress);
		console.log(`[handleDocClick] Download completed successfully for file ID: ${file_id}`);

		const file = doc.getFile();
		if (!file) {
			console.error(`[handleDocClick] No file object returned from download for file ID: ${file_id}`);
			toast("Error", "Unable to retrieve file data, please try again later", "error");
			return;
		}

		console.log(`[handleDocClick] File object retrieved successfully`);

		const mimeType = file.getMimeType();
		const data = file.getContent_asU8();
		const fileName = file.getFileName();

		console.log(`[handleDocClick] File details - Name: ${fileName}, MIME: ${mimeType}, Data length: ${data.length}`);

		if (!data || data.length === 0) {
			console.error(`[handleDocClick] File data is empty for file ID: ${file_id}`);
			toast("Error", "File appears to be empty, please try again later", "error");
			return;
		}

		console.log(`[handleDocClick] Creating blob for file: ${fileName}`);
		const blob = new Blob([new Uint8Array(data)], { type: mimeType });
		console.log(`[handleDocClick] Blob created successfully - Size: ${blob.size} bytes, Type: ${blob.type}`);

		console.log(`[handleDocClick] Creating object URL for blob`);
		const fileURL = window.URL.createObjectURL(blob);
		console.log(`[handleDocClick] Object URL created: ${fileURL}`);

		// If open in a new tab
		if (openNewTab) {
			console.log(`[handleDocClick] Opening file in new tab`);
			try {
				const tab = window.open();
				if (tab) {
					console.log(`[handleDocClick] New tab opened successfully, setting location`);
					tab.location.href = fileURL;
					console.log(`[handleDocClick] File should now be opening in new tab`);
				} else {
					console.error(`[handleDocClick] Failed to open new tab - popup blocker or browser restriction`);
					toast("Error", "Unable to open new tab. Please check popup blocker settings.", "error");
				}
			} catch (tabError) {
				console.error(`[handleDocClick] Error opening new tab:`, tabError);
				toast("Error", "Unable to open file in new tab, please try again", "error");
			}
		} else {
			console.log(`[handleDocClick] Opening file in same tab or downloading`);
			// Else allow download in the same tab
			if (downloadFileName) {
				console.log(`[handleDocClick] Creating download link with filename: ${downloadFileName}`);
				try {
					// Create an anonymous anchor link, if a custom download file name is requested
					const link = document.createElement("a");
					link.setAttribute("href", URL.createObjectURL(blob));
					link.setAttribute("download", downloadFileName);
					link.style.visibility = "hidden";

					// Append the anchor, click it, and remove it
					document.body.appendChild(link);
					console.log(`[handleDocClick] Triggering download`);
					link.click();
					document.body.removeChild(link);
					console.log(`[handleDocClick] Download triggered successfully`);
				} catch (downloadError) {
					console.error(`[handleDocClick] Error during download:`, downloadError);
					toast("Error", "Unable to download file, please try again", "error");
				}
			} else {
				console.log(`[handleDocClick] Opening file in same window`);
				try {
					window.open(fileURL);
					console.log(`[handleDocClick] File opened in same window`);
				} catch (openError) {
					console.error(`[handleDocClick] Error opening file in same window:`, openError);
					toast("Error", "Unable to open file, please try again", "error");
				}
			}
		}
	} catch (e) {
		console.error(`[handleDocClick] Unexpected error for file ID: ${file_id}:`, e);
		toast("Error", "Unable to open file, please try again later", "error");
	}
};

export interface LocalFileIdentifier extends FileIdentifierType {
	isDeleted?: boolean;
}

/** A helper function that capitalizes the first letter of the string */
export const capitalizeFirstLetter = (str: string | undefined) => {
	return str && str.length ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : str;
};

/**
 * A helper function that converts a mixed case name into a human readable label
 *
 * For example: `ProofOfFullTimeSchoolEnrollment` will become `Proof Of Full Time School Enrollment`
 */
export const formatMixedCaseName = (str: string) => {
	let value = str;
	if (value && value.length) {
		value = value
			.replaceAll(" ", "") // remove all space, if any exist
			.split(/(?=[A-Z])/) // split by capital letters
			.map((part) => capitalizeFirstLetter(part)) // capitalize each part
			.join(" "); // combine back into a single string
	}
	return value;
};

/**
 * A helper function that converts an upper snake case name into a human readable label
 *
 * For example: `QUEUED_FOR_PAYMENT` will become `Queued For Payment`
 */
export const formatSnakeCaseName = (str: string) => {
	let value = str;
	if (value && value.length) {
		value = value
			.toLowerCase()
			.split("_") // split the string up
			.map((part) => capitalizeFirstLetter(part)) // capitalize each part
			.join(" "); // combine back into a single string
	}
	return value;
};

export const parseRpcError = (errorMessage: string): ErrorResponse => {
	try {
		// check if error message is a stringified JSON
		if (!errorMessage.startsWith("{")) {
			return {
				errorCode: "unknown",
				errorMessage: errorMessage
			};
		} else {
			// Attempt to parse the JSON string
			const parsedError = JSON.parse(errorMessage);

			// Convert snake_case to camelCase
			return {
				errorCode: parsedError.error_code,
				errorMessage: parsedError.error_message
			};
		}
	} catch (err) {
		console.error("Failed to parse error message:", err);

		return {
			errorCode: "unknown",
			errorMessage: "An unknown error occurred. Please try again later."
		};
	}
};

/**
 * Debounce function to limit the rate at which a function can fire
 * @param fn The function to debounce
 * @param delay The delay in milliseconds
 * @returns A debounced version of the function
 */
export const debounce = <T extends (...args: any[]) => any>(
	fn: T,
	delay: number
): ((...args: Parameters<T>) => void) => {
	let timeoutId: ReturnType<typeof setTimeout> | null = null;

	return (...args: Parameters<T>): void => {
		if (timeoutId) {
			clearTimeout(timeoutId);
		}

		timeoutId = setTimeout(() => {
			fn(...args);
			timeoutId = null;
		}, delay);
	};
};

/**
 * Helper function to validate if a password meets the required criteria
 * @param password The password to validate
 * @returns boolean indicating if the password is valid
 */
export const isPasswordValid = (password: string): boolean => {
	return password.length >= 8 && password.length <= 64;
};

export const doesUserHaveRoles = (currentUser: User | undefined, roles: Array<UserRole>): boolean => {
	if (!currentUser) return false;

	let hasRole: boolean = false;
	const roleStrings: Array<string> = roles.map(String);
	currentUser.role.split(",").forEach((role: string) => {
		if (roleStrings.indexOf(role) > -1) hasRole = true;
	});

	return hasRole;
};

/**
 * Format file size in bytes to human readable format
 * @param bytes File size in bytes
 * @returns Formatted string (e.g., "1.5 MB", "500 KB")
 */
export const formatFileSize = (bytes: number): string => {
	if (bytes === 0) return "0 Bytes";

	const k = 1024;
	const sizes = ["Bytes", "KB", "MB", "GB"];
	const i = Math.floor(Math.log(bytes) / Math.log(k));

	return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

/**
 * Validate if a single file size is within limits
 * @param fileSize File size in bytes
 * @param fileName Optional file name for error messages
 * @returns Object with validation result and error message
 */
export const validateFileSize = (fileSize: number, fileName?: string): { isValid: boolean; error?: string } => {
	console.log(`[validateFileSize] Validating file size: ${fileSize} bytes for file: ${fileName}`);
	console.log(`[validateFileSize] MAX_FILE_SIZE: ${FILE_SIZE_LIMITS.MAX_FILE_SIZE}`);
	console.log(
		`[validateFileSize] File size: ${fileSize} is greater than MAX_FILE_SIZE: ${FILE_SIZE_LIMITS.MAX_FILE_SIZE}? ${fileSize > FILE_SIZE_LIMITS.MAX_FILE_SIZE}`
	);
	if (fileSize > FILE_SIZE_LIMITS.MAX_FILE_SIZE) {
		console.log(
			`[validateFileSize] File is too large. Maximum file size is ${formatFileSize(FILE_SIZE_LIMITS.MAX_FILE_SIZE)}.`
		);
		return {
			isValid: false,
			error: `File ${fileName ? `"${fileName}"` : ""} is too large. Maximum file size is ${formatFileSize(FILE_SIZE_LIMITS.MAX_FILE_SIZE)}.`
		};
	}
	return { isValid: true };
};

/**
 * Calculate total size of all documents in a claim
 * @param documents Array of claim documents
 * @returns Total size in bytes
 */
export const calculateClaimTotalSize = (documents: ClaimDocument[]): number => {
	return documents.filter((doc) => !doc.isDeleted).reduce((total, doc) => total + (doc.fileSize || 0), 0);
};

/**
 * Validate if adding a new file would exceed claim size limits
 * @param currentDocuments Current claim documents
 * @param newFileSize Size of the new file to be added
 * @returns Object with validation result and error message
 */
export const validateClaimSize = (
	currentDocuments: ClaimDocument[],
	newFileSize: number
): { isValid: boolean; error?: string } => {
	const currentTotalSize = calculateClaimTotalSize(currentDocuments);
	const newTotalSize = currentTotalSize + newFileSize;

	if (newTotalSize > FILE_SIZE_LIMITS.MAX_CLAIM_SIZE) {
		return {
			isValid: false,
			error: `Adding this file would exceed the claim size limit. Current total: ${formatFileSize(currentTotalSize)}, New total would be: ${formatFileSize(newTotalSize)}. Maximum claim size is ${formatFileSize(FILE_SIZE_LIMITS.MAX_CLAIM_SIZE)}.`
		};
	}

	return { isValid: true };
};

export const processClaimStatusForAttorney = (currentStatus: ClaimStatus | undefined): string => {
	if (!currentStatus) return "";

	if ([ClaimStatus.PendingVerification, ClaimStatus.PendingInternalReview].includes(currentStatus)) {
		return "In Review";
	}

	if (currentStatus === ClaimStatus.ClosedNotPaidGoodwill) {
		return ClaimStatus.ClosedNotPaid;
	}

	return currentStatus;
};

export const getAttorneyClaimStatusFilterOptions = (): Array<CustomColumnFilterOption> => {
	const groupedStatuses = new Map<string, Array<string>>();

	Object.entries(BffClaimsStatus).forEach(([key, value]) => {
		const visibleStatus = processClaimStatusForAttorney(getClaimStatusFromBffStatus(value as BffClaimsStatus));
		const filterValue = key.replaceAll("_", "").toLowerCase();
		const existingValues = groupedStatuses.get(visibleStatus) ?? [];

		groupedStatuses.set(visibleStatus, [...existingValues, filterValue]);
	});

	return Array.from(groupedStatuses.entries())
		.map(([label, values]) => ({
			label,
			value: values.join("|")
		}))
		.sort((a, b) => a.label.localeCompare(b.label));
};
